import { useCallback, useEffect, useMemo, useState } from 'react';
import SimpleModal from '../SimpleModal';
import { bulkSetField } from '../../api/channelBulk';
import { fetchChannelsIndex, type ChannelRow } from '../../api/channels';
import { getApiErrorMessage } from '../../api/http';
import { useToast } from '../../context/ToastContext';
import { useUndoRedo } from '../../context/UndoRedoContext';
import { useBasicEditorToolContext } from '../../context/EditorSessionContext';
import { usePlaylist } from '../../context/PlaylistContext';

function normCat(c: string | null | undefined) {
  if (c == null || c === '') return 'UNCATEGORIZED';
  const v = String(c).trim().toUpperCase();
  if (v === 'UNCATEGORIZED') return 'UNCATEGORIZED';
  return v;
}

function applyCase(s: string, mode: 'upper' | 'lower' | 'title'): string {
  if (mode === 'upper') return s.toUpperCase();
  if (mode === 'lower') return s.toLowerCase();
  return s.replace(/\S+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

export default function CaseConvertModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const { showToast } = useToast();
  const { record, refreshEditor } = useUndoRedo();
  const { selectedIds, gridActive } = useBasicEditorToolContext();
  const { playlistId } = usePlaylist();
  const [channels, setChannels] = useState<ChannelRow[]>([]);
  const [mode, setMode] = useState<'upper' | 'lower' | 'title'>('upper');
  const [step, setStep] = useState<1 | 2>(1);
  const [scope, setScope] = useState<'all' | 'selected' | 'categories'>('all');
  const [catPick, setCatPick] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  const categories = useMemo(() => {
    const s = new Set<string>();
    for (const c of channels) s.add(normCat(c.category));
    return [...s].sort((a, b) => a.localeCompare(b, 'en'));
  }, [channels]);

  const load = useCallback(async () => {
    if (!playlistId) return;
    try {
      const rows = await fetchChannelsIndex(playlistId);
      setChannels(rows);
      const init: Record<string, boolean> = {};
      for (const c of rows) init[normCat(c.category)] = true;
      setCatPick(init);
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Failed to fetch channels.'), 'error');
    }
  }, [playlistId, showToast]);

  useEffect(() => {
    if (!show) return;
    setMode('upper');
    setStep(1);
    if (selectedIds.length > 0 && gridActive) setScope('selected');
    else setScope('all');
    void load();
  }, [show, load, selectedIds.length, gridActive]);

  const demoBefore = 'Demo stream name';
  const demoAfter = useMemo(() => applyCase(demoBefore, mode), [mode]);

  function targetRows(): ChannelRow[] {
    if (scope === 'selected') {
      const set = new Set(selectedIds.map(Number));
      return channels.filter((ch) => set.has(Number(ch.id)));
    }
    if (scope === 'categories') {
      const cats = Object.entries(catPick)
        .filter(([, v]) => v)
        .map(([k]) => k);
      const cset = new Set(cats);
      return channels.filter((ch) => cset.has(normCat(ch.category)));
    }
    return channels;
  }

  async function apply() {
    if (!playlistId) {
      showToast('Select a playlist first.', 'warning');
      return;
    }
    const pid = parseInt(playlistId, 10);

    if (scope === 'selected') {
      if (!gridActive || selectedIds.length === 0) {
        showToast('Select channels in the Basic editor.', 'warning');
        return;
      }
    } else if (scope === 'categories') {
      const categoriesScope = Object.entries(catPick)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (categoriesScope.length === 0) {
        showToast('Select at least one category.', 'warning');
        return;
      }
    }

    const rows = targetRows();
    const undoValuesById: Record<string, string> = {};
    const redoValuesById: Record<string, string> = {};
    for (const ch of rows) {
      const id = Number(ch.id);
      if (!Number.isFinite(id)) continue;
      const oldName = String(ch.name ?? '');
      const newName = applyCase(oldName, mode);
      if (newName === oldName) continue;
      undoValuesById[id] = oldName;
      redoValuesById[id] = newName;
    }

    if (Object.keys(redoValuesById).length === 0) {
      showToast('No names would change.', 'warning');
      return;
    }

    const modeLabel = mode === 'upper' ? 'UPPERCASE' : mode === 'lower' ? 'lowercase' : 'Title Case';

    const updates = Object.entries(redoValuesById).map(([id, value]) => ({
      id: parseInt(id, 10),
      value,
    }));

    setBusy(true);
    try {
      const res = await bulkSetField({ playlist_id: pid, field: 'name', updates });
      if (!res.success) {
        showToast(res.message || 'Update failed.', 'error');
        return;
      }
      showToast(res.message || 'Names updated.', 'success');
      record({
        label: `Case: ${modeLabel}`,
        undo: { type: 'setField', playlist_id: pid, field: 'name', valuesById: undoValuesById },
        redo: { type: 'setField', playlist_id: pid, field: 'name', valuesById: redoValuesById },
      });
      onClose();
      await refreshEditor();
    } catch (e) {
      showToast(getApiErrorMessage(e, 'Server error.'), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <SimpleModal
      show={show}
      onClose={onClose}
      title="Convert name case"
      size="lg"
      footer={
        step === 2 ? (
          <div className="d-flex justify-content-between w-100">
            <button type="button" className="btn btn-secondary" onClick={() => setStep(1)}>
              Previous
            </button>
            <div className="d-flex gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="btn btn-success" disabled={busy} onClick={() => void apply()}>
                {busy ? <span className="spinner-border spinner-border-sm" /> : 'Apply'}
              </button>
            </div>
          </div>
        ) : (
          <div className="d-flex justify-content-end gap-2 w-100">
            <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setStep(2)}>
              Next
            </button>
          </div>
        )
      }
    >
      <div className="progress mb-3" style={{ height: 6 }}>
        <div className="progress-bar bg-primary" style={{ width: step === 1 ? '50%' : '100%' }} />
      </div>

      {step === 1 ? (
        <>
          <p className="small fw-semibold text-uppercase text-muted mb-2">Step 1</p>
          <label className="form-label">Convert streams name to a specific case format.</label>
          <div className="small text-muted mb-3">
            {demoBefore} -&gt; {demoAfter}
          </div>

          <div className="form-check">
            <input className="form-check-input" type="radio" id="ccUpper" checked={mode === 'upper'} onChange={() => setMode('upper')} />
            <label className="form-check-label" htmlFor="ccUpper">
              UPPERCASE
            </label>
          </div>
          <div className="form-check mt-2">
            <input className="form-check-input" type="radio" id="ccLower" checked={mode === 'lower'} onChange={() => setMode('lower')} />
            <label className="form-check-label" htmlFor="ccLower">
              lowercase
            </label>
          </div>
          <div className="form-check mt-2">
            <input className="form-check-input" type="radio" id="ccTitle" checked={mode === 'title'} onChange={() => setMode('title')} />
            <label className="form-check-label" htmlFor="ccTitle">
              Title Mode
            </label>
          </div>
        </>
      ) : (
        <>
          <p className="small fw-semibold text-uppercase text-muted mb-2">Step 2</p>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              id="ccSel"
              checked={scope === 'selected'}
              disabled={!gridActive || selectedIds.length === 0}
              onChange={() => setScope('selected')}
            />
            <label className="form-check-label" htmlFor="ccSel">
              {selectedIds.length} selected streams
            </label>
          </div>
          <div className="form-check mt-2">
            <input className="form-check-input" type="radio" id="ccCat" checked={scope === 'categories'} onChange={() => setScope('categories')} />
            <label className="form-check-label" htmlFor="ccCat">
              Streams from categories
            </label>
          </div>
          <div className="form-check mt-2 mb-2">
            <input className="form-check-input" type="radio" id="ccAll" checked={scope === 'all'} onChange={() => setScope('all')} />
            <label className="form-check-label" htmlFor="ccAll">
              All streams
            </label>
          </div>

          {scope === 'categories' ? (
            <div className="border rounded p-2 mb-0 bg-light bg-opacity-50" style={{ maxHeight: 160, overflowY: 'auto' }}>
              {categories.map((c) => {
                const display = c.toUpperCase();
                return (
                  <div className="form-check" key={c}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={`cc-${c}`}
                      checked={!!catPick[c]}
                      onChange={(e) => setCatPick((p) => ({ ...p, [c]: e.target.checked }))}
                    />
                    <label className="form-check-label small" htmlFor={`cc-${c}`}>
                      {display}
                    </label>
                  </div>
                );
              })}
            </div>
          ) : null}
        </>
      )}
    </SimpleModal>
  );
}
